import { useState, useRef, useEffect, useId } from "react";
import "./MetricValue.css";
import { formatSmartCurrency, formatFullCurrency, toNumber } from "../../utils/format";

/**
 * MetricValue. Renders a numeric value with automatic abbreviation to fit its
 * container. If the number is abbreviated (Section 6.2 of `project-brief-v1`)
 * a hover, focus, or long-press tooltip reveals the full precise value.
 *
 * Props:
 *   value          number or numeric string (required).
 *   mode           "kpi" (minus sign) or "table" (parentheses). Default "kpi".
 *   charBudget     Threshold above which the value is abbreviated. Default 12.
 *   decimals       Precision for the full value. Default 2 for currency.
 *   asCurrency     When true (default) formats with `$`. Set false for plain integers.
 *   className      Optional class to style the displayed value.
 *   testid         Optional data-testid override. Default "metric-value".
 *
 * A11y and touch:
 *   Full precise value is exposed via `aria-label` on the value span so screen
 *   readers always read the full number even if visual is abbreviated.
 *   Tap-and-hold (touchstart) reveals the tooltip on mobile.
 *   Hover and focus reveal on desktop with a 300ms delay.
 */
export default function MetricValue({
  value,
  mode = "kpi",
  charBudget = 12,
  decimals = 2,
  asCurrency = true,
  className,
  testid = "metric-value",
}) {
  const [open, setOpen] = useState(false);
  const [flip, setFlip] = useState("top");
  const wrapRef = useRef(null);
  const popRef = useRef(null);
  const timerRef = useRef(null);
  const tooltipId = useId();

  const n = toNumber(value);
  let display;
  let full;
  let wasAbbrev;

  if (n === null) {
    display = "--";
    full = "--";
    wasAbbrev = false;
  } else if (asCurrency) {
    const smart = formatSmartCurrency(n, { mode, charBudget, decimals });
    display = smart.display;
    full = smart.full;
    wasAbbrev = smart.wasAbbreviated;
  } else {
    display = n.toLocaleString("en-US");
    full = n.toLocaleString("en-US");
    wasAbbrev = false;
  }

  const cancelShow = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };
  const scheduleShow = () => {
    if (!wasAbbrev) return;
    cancelShow();
    timerRef.current = setTimeout(() => setOpen(true), 300);
  };
  const hide = () => {
    cancelShow();
    setOpen(false);
  };

  useEffect(() => {
    if (!open || !wrapRef.current || !popRef.current) return;
    const wr = wrapRef.current.getBoundingClientRect();
    const wh = window.innerHeight;
    if (wr.top < popRef.current.offsetHeight + 16 && wh - wr.bottom > popRef.current.offsetHeight + 16) {
      setFlip("bottom");
    } else {
      setFlip("top");
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") hide(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  return (
    <span
      ref={wrapRef}
      className={`mv-wrap ${wasAbbrev ? "mv-has-tooltip" : ""}`}
      onMouseEnter={scheduleShow}
      onMouseLeave={hide}
      onFocus={scheduleShow}
      onBlur={hide}
      onTouchStart={scheduleShow}
      onTouchEnd={() => setTimeout(hide, 1200)}
      tabIndex={wasAbbrev ? 0 : -1}
      aria-describedby={open ? tooltipId : undefined}
      data-testid={testid}
    >
      <span className={className} aria-label={wasAbbrev ? full : undefined}>
        {display}
      </span>
      {wasAbbrev && open && (
        <span
          ref={popRef}
          id={tooltipId}
          role="tooltip"
          className={`mv-tooltip mv-tooltip-${flip}`}
          data-testid={`${testid}-full`}
        >
          {full}
        </span>
      )}
    </span>
  );
}

/**
 * Static helper. Same rules as MetricValue but returns a plain string.
 * Use in chart axis formatters and other places React nodes are not accepted.
 */
export function formatMetric(value, { mode = "kpi", charBudget = 12, decimals = 2, asCurrency = true } = {}) {
  const n = toNumber(value);
  if (n === null) return "--";
  if (!asCurrency) return n.toLocaleString("en-US");
  if (charBudget <= 0) return formatFullCurrency(n, { mode, decimals });
  return formatSmartCurrency(n, { mode, charBudget, decimals }).display;
}
